import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCcw,
  Rocket,
  Search,
  SearchX,
  Send,
} from "lucide-react";
import {
  errorMessage,
  getLaunchReadiness,
  launchCampaign,
  listCampaignContacts,
  type CampaignContact,
  type LaunchReadiness,
  type LaunchResult,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { ContactStatusBadge, Hint } from "@/components/email/EmailBadges";
import { CompanyCell, EmailCell, LeadCell } from "@/components/email/LeadCells";
import { LeadIssues } from "@/components/email/LeadIssues";
import { SelectAllBox } from "@/components/email/SelectAllBox";
import { StepProgress } from "@/components/email/StepProgress";
import { useDebounce } from "@/hooks/useDebounce";
import { EVENT_LABEL, chunks, launchHasMore, launchSummary, mergeLaunch, plural, shortAgo } from "@/lib/email";
import { formatDateTime, formatNumber } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/pages/email/CampaignWorkspace";

const PAGE_SIZE = 50;
/** Leads per launch call: each one's emails are rendered (AI passages
 *  written) before it is handed over. */
const BATCH = 20;
const MAX_ROUNDS = 50;

type View = "to-launch" | "launched";
type ReadinessFilter = "all" | "ready" | "blocked";

/**
 * Launch: the leads prepared in Leads, handed to Lemlist — the only way a
 * new lead reaches it. To launch shows what the backend's rules say of each
 * one (READY TO LAUNCH, or BLOCKED and why), read without Lemlist and
 * without a model; Launched shows those Lemlist holds, each opening its
 * frozen sequence. The backend checks every lead again when it launches it.
 */
export default function LaunchTab() {
  const [params, setParams] = useSearchParams();
  const view: View = params.get("view") === "launched" ? "launched" : "to-launch";
  const { campaign, version } = useWorkspace();
  const [launchedCount, setLaunchedCount] = useState<number | null>(null);
  const [toLaunchCount, setToLaunchCount] = useState<number | null>(null);

  function setView(next: View) {
    const merged = new URLSearchParams(params);
    if (next === "to-launch") merged.delete("view");
    else merged.set("view", next);
    setParams(merged, { replace: true });
  }

  return (
    <div className="space-y-3">
      <div
        role="tablist"
        aria-label="Launch"
        className="inline-flex rounded-lg bg-white p-0.5 shadow-sm-tile ring-1 ring-slate-900/[0.04]"
      >
        {(
          [
            { value: "to-launch", label: "To launch", count: toLaunchCount },
            { value: "launched", label: "Launched", count: launchedCount },
          ] as const
        ).map((option) => (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={view === option.value}
            onClick={() => setView(option.value)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              view === option.value ? "bg-indigo-600 text-white shadow-cta" : "text-slate-500 hover:text-slate-800",
            )}
          >
            {option.label}
            {option.count !== null && (
              <span
                className={cn(
                  "rounded-full px-1.5 text-[11px] tabular-nums",
                  view === option.value ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500",
                )}
              >
                {formatNumber(option.count)}
              </span>
            )}
          </button>
        ))}
      </div>

      {view === "to-launch" ? (
        <ToLaunch
          key={`${campaign.id}-${version}`}
          onCounts={(toLaunch, launched) => {
            setToLaunchCount(toLaunch);
            setLaunchedCount(launched);
          }}
        />
      ) : (
        <Launched key={`${campaign.id}-${version}`} onCount={setLaunchedCount} />
      )}
    </div>
  );
}

function ToLaunch({ onCounts }: { onCounts: (toLaunch: number, launched: number) => void }) {
  const confirm = useConfirm();
  const { slug, campaign, access, external, openLead, changed } = useWorkspace();
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 250);
  const [filter, setFilter] = useState<ReadinessFilter>("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<LaunchReadiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [running, setRunning] = useState(false);

  useEffect(() => setPage(1), [debounced, filter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await getLaunchReadiness(slug, campaign.id, {
        search: debounced.trim(),
        readiness: filter,
        page,
        page_size: PAGE_SIZE,
      });
      setData(next);
      onCounts(next.to_launch, next.launched);
      // A selection only ever holds leads that can still be launched.
      setSelected((prev) => new Set([...prev].filter((id) => next.ready_ids.includes(id))));
    } catch (err) {
      setError(errorMessage(err, "The leads to launch could not be loaded."));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onCounts is the parent's setter pair
  }, [slug, campaign.id, debounced, filter, page]);

  useEffect(() => {
    void load();
  }, [load, reload]);

  const blockingGates = data?.gates.filter((g) => g.blocking) ?? [];
  const warnings = data?.gates.filter((g) => !g.blocking) ?? [];
  const canLaunch = !!data?.can_launch && access.can_sync && !running;
  const launchHint = !access.can_sync
    ? "Launching needs the right to sync with Lemlist (outreach.sync)."
    : blockingGates[0]?.message ?? null;
  const items = data?.items ?? [];
  const pageEligible = items.filter((row) => row.ready).map((row) => row.lead.id);
  const allOnPage = pageEligible.length > 0 && pageEligible.every((id) => selected.has(id));
  const someOnPage = pageEligible.some((id) => selected.has(id));
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  async function run(ids: number[] | null) {
    if (!data) return;
    const count = ids ? ids.length : data.ready;
    const room = data.remaining_today;
    const ok = await confirm({
      title: ids ? `Launch ${plural(count, "lead")}?` : `Launch the ${plural(count, "eligible lead")}?`,
      description: external
        ? `Each lead's emails are rendered — AI passages written now — frozen, and handed to Lemlist, which sends them on the campaign's days and hours. ${
            count > room
              ? `Today's limit leaves room for ${plural(room, "lead")}; the others stay to launch.`
              : ""
          } Every lead is checked again first.`
        : "Dry run: the hand-over is simulated. Nothing is sent to anyone.",
      confirmLabel: external ? "Launch" : "Launch (dry run)",
      variant: external ? "warning" : "info",
    });
    if (!ok) return;
    setRunning(true);
    const toastId = toast.loading(external ? "Launching to Lemlist…" : "Dry run…");
    let total: LaunchResult | null = null;
    try {
      if (ids) {
        for (const batch of chunks(ids, BATCH)) {
          const result = await launchCampaign(slug, campaign.id, { membership_ids: batch, limit: BATCH });
          total = mergeLaunch(total, result);
          toast.loading(`${launchSummary(total)} Continuing…`, { id: toastId });
          if (result.stopped || result.pushed_today >= result.daily_limit) break;
        }
      } else {
        for (let round = 0; round < MAX_ROUNDS; round++) {
          const result = await launchCampaign(slug, campaign.id, { limit: BATCH });
          total = mergeLaunch(total, result);
          if (!launchHasMore(result)) break;
          toast.loading(`${launchSummary(total)} Continuing…`, { id: toastId });
        }
      }
      if (total) {
        toast.success(launchSummary(total), { id: toastId, description: total.notes.join(" ") || undefined });
        const reasons = [...total.failures, ...total.skipped];
        if (reasons.length) {
          toast.warning(`${reasons.length} not launched`, {
            description: reasons
              .slice(0, 3)
              .map((r) => `${r.contact_name || `#${r.membership_id}`}: ${r.reason}`)
              .join(" · "),
          });
        }
      }
      setSelected(new Set());
    } catch (err) {
      toast.error(errorMessage(err, "The launch failed."), { id: toastId });
    } finally {
      setRunning(false);
      setReload((n) => n + 1);
      changed();
    }
  }

  return (
    <div className="space-y-3">
      {data && (blockingGates.length > 0 || warnings.length > 0) && (
        <div className="space-y-1.5">
          {blockingGates.map((gate) => (
            <p
              key={gate.code}
              className="flex items-start gap-2 rounded-xl border border-rose-100 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800"
            >
              <Ban className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{gate.message}</span>
            </p>
          ))}
          {warnings.map((gate) => (
            <p
              key={gate.code}
              className="flex items-start gap-2 rounded-xl border border-amber-100 bg-amber-50/80 px-3.5 py-2 text-xs text-amber-900"
            >
              <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
              <span>{gate.message}</span>
            </p>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-plane bg-white shadow-plane">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3.5 py-3">
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search a lead or a company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 rounded-lg border-slate-200 bg-slate-50/60 pl-9"
              aria-label="Search leads to launch"
            />
          </div>
          <div role="radiogroup" aria-label="Readiness" className="inline-flex rounded-lg border border-slate-200 p-0.5">
            {(
              [
                { value: "all", label: "All", count: data?.to_launch },
                { value: "ready", label: "Ready", count: data?.ready },
                { value: "blocked", label: "Blocked", count: data?.blocked },
              ] as const
            ).map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={filter === option.value}
                onClick={() => setFilter(option.value)}
                className={cn(
                  "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                  filter === option.value ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800",
                )}
              >
                {option.label}
                {option.count !== undefined && <span className="tabular-nums opacity-70">{formatNumber(option.count)}</span>}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={() => setReload((n) => n + 1)} aria-label="Refresh">
            <RefreshCcw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          </Button>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {data && (
              <Hint content="Leads handed to Lemlist today, under the campaign's daily limit (its timezone).">
                <span className="text-xs tabular-nums text-slate-500">
                  {data.launched_today}/{data.daily_limit} today · {plural(data.remaining_today, "left", "left")}
                </span>
              </Hint>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={!data?.ready_ids.length || running}
              onClick={() => setSelected(new Set(data?.ready_ids ?? []))}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Select all eligible
            </Button>
            <Hint content={launchHint}>
              <span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!canLaunch || !selected.size}
                  onClick={() => void run([...selected])}
                >
                  {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Launch selected{selected.size ? ` (${formatNumber(selected.size)})` : ""}
                </Button>
              </span>
            </Hint>
            <Hint content={launchHint}>
              <span>
                <Button variant="primary" size="sm" disabled={!canLaunch || !data?.ready} onClick={() => void run(null)}>
                  {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />}
                  Launch all eligible{data?.ready ? ` (${formatNumber(data.ready)})` : ""}
                </Button>
              </span>
            </Hint>
          </div>
        </div>

        {error ? (
          <div className="p-8 text-center text-sm text-rose-600">{error}</div>
        ) : loading && !data ? (
          <div className="space-y-2 p-4" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full rounded-lg" />
            ))}
          </div>
        ) : !items.length ? (
          debounced.trim() || filter !== "all" ? (
            <div className="flex flex-col items-center px-6 py-14 text-center">
              <SearchX className="h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-medium text-slate-700">No lead matches</p>
            </div>
          ) : (
            <div className="flex flex-col items-center px-6 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                <CheckCircle2 className="h-5 w-5" />
              </span>
              <p className="mt-3 text-sm font-semibold text-slate-800">Nothing to launch</p>
              <p className="mt-1 max-w-sm text-xs text-slate-500">
                {data?.launched
                  ? `Every lead of “${campaign.name}” is launched — add more from the Leads tab.`
                  : `“${campaign.name}” has no lead yet — add some from the Leads tab, then launch them here.`}
              </p>
            </div>
          )
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left">
                  <th className="w-10 px-4 py-2">
                    <SelectAllBox
                      label="Select the eligible leads of this page"
                      all={allOnPage}
                      some={someOnPage}
                      onChange={(on) =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          for (const id of pageEligible) {
                            if (on) next.add(id);
                            else next.delete(id);
                          }
                          return next;
                        })
                      }
                    />
                  </th>
                  {["Lead", "Company", "Email", "Readiness", "Emails"].map((label) => (
                    <th key={label} className="whitespace-nowrap px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.lead.id}
                    onClick={() => openLead(row.lead.id)}
                    className={cn(
                      "cursor-pointer border-b border-slate-50 align-top transition-colors last:border-0 hover:bg-indigo-50/30",
                      selected.has(row.lead.id) && "bg-indigo-50/40",
                    )}
                  >
                    <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                      {row.ready ? (
                        <Checkbox
                          aria-label={`Select ${row.lead.contact_name}`}
                          checked={selected.has(row.lead.id)}
                          onCheckedChange={(checked) =>
                            setSelected((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(row.lead.id);
                              else next.delete(row.lead.id);
                              return next;
                            })
                          }
                          className="border-slate-300 data-[state=checked]:border-indigo-600 data-[state=checked]:bg-indigo-600"
                        />
                      ) : (
                        <Hint content="Blocked: it cannot be launched until the reason goes.">
                          <Ban className="h-4 w-4 text-slate-300" />
                        </Hint>
                      )}
                    </td>
                    <td className="max-w-[190px] px-3 py-2">
                      <LeadCell row={row.lead} />
                    </td>
                    <td className="max-w-[170px] px-3 py-2">
                      <CompanyCell row={row.lead} />
                    </td>
                    <td className="max-w-[210px] px-3 py-2">
                      <EmailCell row={row.lead} />
                    </td>
                    <td className="w-[260px] max-w-[300px] px-3 py-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {row.ready ? (
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                            <CheckCircle2 className="h-3 w-3" /> Ready to launch
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700">
                            <Ban className="h-3 w-3" /> Blocked
                          </span>
                        )}
                        {row.lead.status === "EMAIL_PAUSED" && (
                          <ContactStatusBadge status={row.lead.status} reason={row.lead.status_reason} />
                        )}
                      </div>
                      {row.issues.length > 0 && (
                        <div className="mt-1">
                          <LeadIssues row={row.lead} issues={row.issues} max={3} />
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">
                      <Hint content="Emails already rendered for this lead (a preview freezes them). The others are rendered when it is launched — never by opening this page.">
                        <span className="tabular-nums">
                          {row.steps_rendered}/{row.steps_total}
                        </span>
                      </Hint>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(data?.total ?? 0) > PAGE_SIZE && (
          <Pager page={page} totalPages={totalPages} total={data?.total ?? 0} pageSize={PAGE_SIZE} onPage={setPage} />
        )}
      </div>
    </div>
  );
}

function Launched({ onCount }: { onCount: (count: number) => void }) {
  const { slug, campaign, openLead } = useWorkspace();
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 250);
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<CampaignContact[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => setPage(1), [debounced, includeRemoved]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listCampaignContacts(slug, {
      campaign_id: campaign.id,
      launched: true,
      include_removed: includeRemoved,
      search: debounced.trim(),
      page,
      page_size: PAGE_SIZE,
    })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
        if (!debounced.trim()) onCount(res.total);
      })
      .catch((err) => !cancelled && setError(errorMessage(err, "Launched leads could not be loaded.")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onCount is the parent's setter
  }, [slug, campaign.id, debounced, includeRemoved, page, reload]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="overflow-hidden rounded-plane bg-white shadow-plane">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3.5 py-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search a lead, a company or an email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 rounded-lg border-slate-200 bg-slate-50/60 pl-9"
            aria-label="Search launched leads"
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-500">
          <Switch checked={includeRemoved} onCheckedChange={setIncludeRemoved} />
          Show removed
        </label>
        <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={() => setReload((n) => n + 1)} aria-label="Refresh">
          <RefreshCcw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
        </Button>
        <p className="ml-auto text-xs text-slate-400">Click a lead to read its frozen sequence and its events.</p>
      </div>
      {error ? (
        <div className="p-8 text-center text-sm text-rose-600">{error}</div>
      ) : loading && !items.length ? (
        <div className="space-y-2 p-4" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full rounded-lg" />
          ))}
        </div>
      ) : !items.length ? (
        <div className="flex flex-col items-center px-6 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-500">
            <Rocket className="h-5 w-5" />
          </span>
          <p className="mt-3 text-sm font-semibold text-slate-800">
            {debounced.trim() ? "No launched lead matches" : "No lead launched yet"}
          </p>
          {!debounced.trim() && (
            <p className="mt-1 max-w-sm text-xs text-slate-500">Leads appear here once launched from To launch.</p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left">
                {["Lead", "Company", "State", "Launched", "Last sent", "Last open", "Reply", "Last event"].map((label) => (
                  <th key={label} className="whitespace-nowrap px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => openLead(row.id)}
                  className={cn(
                    "cursor-pointer border-b border-slate-50 align-top transition-colors last:border-0 hover:bg-indigo-50/30",
                    row.status === "EMAIL_REMOVED" && "opacity-60",
                  )}
                >
                  <td className="max-w-[220px] px-3 py-2">
                    <p className="truncate font-medium text-slate-900">{row.contact_name}</p>
                    <div className="text-xs">
                      <EmailCell row={row} showVerification={false} />
                    </div>
                  </td>
                  <td className="max-w-[170px] px-3 py-2">
                    <CompanyCell row={row} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <ContactStatusBadge status={row.status} reason={row.status_reason} />
                      {row.dry_run && (
                        <Hint content="Launched in a dry run: nothing was sent.">
                          <span className="rounded border border-amber-200 bg-amber-50 px-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                            Dry
                          </span>
                        </Hint>
                      )}
                    </div>
                    <div className="mt-1">
                      <StepProgress current={row.current_step} total={row.steps_count} />
                    </div>
                  </td>
                  <TimeCell value={row.queued_at} />
                  <TimeCell value={row.last_sent_at} />
                  <TimeCell value={row.last_opened_at} />
                  <TimeCell value={row.last_replied_at} />
                  <td className="whitespace-nowrap px-3 py-2 text-xs">
                    {row.last_event_type ? (
                      <span className="text-slate-700" title={formatDateTime(row.last_event_at)}>
                        {EVENT_LABEL[row.last_event_type] ?? row.last_event_type}
                        <span className="text-slate-400"> · {shortAgo(row.last_event_at)}</span>
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > PAGE_SIZE && <Pager page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} onPage={setPage} />}
    </div>
  );
}

function TimeCell({ value }: { value: string | null }) {
  return (
    <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-600" title={value ? formatDateTime(value) : undefined}>
      {value ? shortAgo(value) : <span className="text-slate-300">—</span>}
    </td>
  );
}

function Pager({
  page,
  totalPages,
  total,
  pageSize,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPage: (page: number) => void;
}) {
  return (
    <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
      <span>
        {formatNumber((page - 1) * pageSize + 1)}–{formatNumber(Math.min(page * pageSize, total))} of {formatNumber(total)}
      </span>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page">
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <span className="px-2 tabular-nums">
          {page} / {totalPages}
        </span>
        <Button variant="outline" size="icon" className="h-7 w-7" disabled={page >= totalPages} onClick={() => onPage(page + 1)} aria-label="Next page">
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
